/**
 * 히어로 장식(모자·소품) 놓을 자리 — 운영자 조절값 로드.
 *
 * 코드 기본값(`shared/heroOverlayFit`) **위에 덮어쓰는** 문서다. 못 받아 와도
 * 아바타는 기본값으로 그려진다(불변식 3) — 그래서 실패를 조용히 삼켜도 되는
 * **의도된 경계**다. 여기서 throw 하면 서버가 없는 M1 에서 아바타가 안 나온다.
 *
 * 한 번 받아 캐시한다 — 렌더러가 프레임마다 부르기 때문. 관리자가 새로 게시하면
 * 실시간 푸시(`overlayfit`)가 캐시를 비운다.
 */
import { sanitizeOverlayFitDoc, overlayFitKeys, type OverlayFitDoc } from "@greg-chick/shared";
import { EDGE_BASE } from "./apiBase";

let cache: OverlayFitDoc = {};
/** **받아 온 적이 있는가.** 실패는 여기에 기록하지 않는다 — 아래 설명. */
let loaded = false;
/** 같은 요청이 겹치지 않게(아바타·월드가 동시에 뜬다). */
let inflight: Promise<OverlayFitDoc> | null = null;
/** 실패 후 잠깐 쉬는 시각 — 엣지가 없는 M1 에서 존을 옮길 때마다 2초씩 기다리지 않게. */
let retryAfter = 0;
const RETRY_COOLDOWN_MS = 5000;
const DEADLINE_MS = 2000;

/**
 * 실패하면 **스스로 몇 번 다시 받아 본다.**
 *
 * 부르는 쪽이 다시 불러 줄 거라고 기대할 수 없다 — 랜딩의 아바타는 뜰 때 한 번 부르고
 * 그걸로 끝이다. 서버를 막 켜서 첫 요청이 늦으면(콜드 스타트) 그 화면은 영영 기본값이다.
 *
 * 무한정 두드리지는 않는다. 엣지 없이 도는 게 정상인 M1 도 있다(불변식 3).
 */
const RETRY_DELAYS_MS = [1500, 5000];
let retriesLeft = RETRY_DELAYS_MS.length;
/** 늦게 받아 왔을 때 화면에 알려 준다(월드는 매 프레임 캐시를 보므로 저절로 따라온다). */
const listeners = new Set<(doc: OverlayFitDoc) => void>();

/** 늦게 도착한 조절값 받기. 반환값 호출로 해지. */
export function onOverlayFitReady(cb: (doc: OverlayFitDoc) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** 지금 적용할 조절값(동기) — 아직 안 받았으면 빈 문서(=코드 기본값). */
export function overlayFitDoc(): OverlayFitDoc {
  return cache;
}

/**
 * 게시본을 받아 온다. 한 번 **성공**하면 그걸 계속 쓴다.
 *
 * ## 실패를 성공처럼 기억하면 안 된다
 * 예전엔 `loaded = true` 를 fetch **앞에서** 켰다. 그래서 한 번이라도 못 받으면 —
 * 서버를 방금 켠 직후(첫 요청이 마감시간 2초를 넘긴다)나 잠깐 끊겼을 때 —
 * **그 화면이 살아 있는 내내 기본값**으로 그렸다. 다시 받아 볼 기회가 없었다.
 *
 * 증상은 "관리자에서 맞추고 올리면 반영되는데(실시간 푸시), **다시 시작하면 원래대로
 * 돌아온다**" 였다. 값은 멀쩡히 저장돼 있는데 화면이 못 읽은 것이다(2026-07-31).
 */
export async function loadOverlayFit(): Promise<OverlayFitDoc> {
  if (loaded) return cache;
  if (inflight) return inflight;
  if (Date.now() < retryAfter) return cache; // 방금 실패 — 잠깐 쉬고 다음에 다시
  inflight = (async () => {
    try {
      // ⏱️ 마감시간 — 엣지가 **응답을 안 하고 붙잡고 있으면** `create()` 가 여기서 멈춘다.
      // 그러면 아바타가 아예 안 생겨 월드 전체가 정지한 것처럼 보인다(불변식 3).
      // 못 받으면 코드 기본값으로 그린다 — 조절값은 있으면 좋은 것이지 필수가 아니다.
      const ac = new AbortController();
      const timer = setTimeout(() => ac.abort(), DEADLINE_MS);
      const res = await fetch(`${EDGE_BASE}/api/overlay-fit`, {
        cache: "no-store",
        signal: ac.signal,
      }).finally(() => clearTimeout(timer));
      if (res.ok) {
        const body = (await res.json()) as { fit?: unknown };
        cache = sanitizeOverlayFitDoc(body.fit, overlayFitKeys());
        loaded = true; // **여기서만** 켠다 — 받아 온 뒤
        listeners.forEach((l) => l(cache));
        return cache;
      }
    } catch {
      /* 엣지 없음(M1) — 코드 기본값으로 그리고, 다음 기회에 다시 받아 본다 */
    }
    retryAfter = Date.now() + RETRY_COOLDOWN_MS;
    if (retriesLeft > 0) {
      const wait = RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - retriesLeft]!;
      retriesLeft--;
      setTimeout(() => {
        retryAfter = 0; // 스스로 다시 받아 볼 때는 쉬는 시간을 건너뛴다
        void loadOverlayFit();
      }, wait);
    }
    return cache;
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

/** 관리자가 새로 게시했을 때 — 다음 로드가 다시 받아 오게. */
export function resetOverlayFitCache(): void {
  cache = {};
  loaded = false;
  // 쉬는 시간도 푼다 — 운영자가 방금 올렸는데 "5초 뒤에요" 라고 할 수는 없다
  retryAfter = 0;
  retriesLeft = RETRY_DELAYS_MS.length;
}
